import { z } from 'zod';
import { usdMinor, type UsdMinor } from './money';

export const CONVERSATION_WINDOW_HOURS = 24;

export const planCodeSchema = z.enum([
  'initial',
  'growth',
  'business',
  'high_volume',
]);
export type PlanCode = z.infer<typeof planCodeSchema>;

export interface CapacityPlan {
  code: PlanCode;
  monthlyPriceUsdMinor: UsdMinor;
  includedConversations: number;
  websites: 1;
}

export const CAPACITY_PLANS: Readonly<Record<PlanCode, CapacityPlan>> = {
  initial: {
    code: 'initial',
    monthlyPriceUsdMinor: usdMinor(5_000n),
    includedConversations: 500,
    websites: 1,
  },
  growth: {
    code: 'growth',
    monthlyPriceUsdMinor: usdMinor(8_000n),
    includedConversations: 1_500,
    websites: 1,
  },
  business: {
    code: 'business',
    monthlyPriceUsdMinor: usdMinor(20_000n),
    includedConversations: 5_000,
    websites: 1,
  },
  high_volume: {
    code: 'high_volume',
    monthlyPriceUsdMinor: usdMinor(50_000n),
    includedConversations: 15_000,
    websites: 1,
  },
};

// The client is warned once four fifths of a period's conversations are used.
export function planCapacityState(
  used: number,
  included: number
): 'available' | 'low' | 'exhausted' {
  if (used >= included) return 'exhausted';
  return used * 5 >= included * 4 ? 'low' : 'available';
}

// What a subscription still admits. Inactive: no active period covers this
// moment, so no new conversation is admitted whatever is left.
export const planCapacitySchema = z
  .object({
    state: z.enum(['available', 'low', 'exhausted', 'inactive']),
    used: z.number().int().nonnegative(),
    included: z.number().int().positive(),
    periodEndsAt: z.iso.datetime({ offset: true }),
  })
  .strict();
export type PlanCapacity = z.infer<typeof planCapacitySchema>;

const MILLISECONDS_PER_HOUR = 60 * 60 * 1_000;

export const conversationWindowEndsAt = (startedAt: Date): Date => {
  if (Number.isNaN(startedAt.getTime())) {
    throw new Error('Conversation start time must be valid.');
  }
  return new Date(
    startedAt.getTime() + CONVERSATION_WINDOW_HOURS * MILLISECONDS_PER_HOUR
  );
};
